import { useMemo } from 'react'
import { keepPreviousData, useInfiniteQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'

import type { DataMeta } from '@/api/types'

import type { Paging } from './ListStates'
import { foldPages, nextOffset, type PagedList } from './paging'

/** Rows per request, for every Analytics list; "Show more" asks for the next page at the same size. */
export const PAGE_SIZE = 50

/**
 * A server-sorted, server-paged list's pages: read by offset at `PAGE_SIZE` (so the list
 * has no ceiling at the API's largest `limit`, and each request stays one page), folded into one list, each
 * identified row once. Keyed by the query less its page: `useInfiniteQuery` keeps the
 * pages within one entry.
 */
export function useServerList<Row extends object, List extends PagedList<Row>>({
  queryKey,
  fetchPage,
  identify,
}: {
  queryKey: QueryKey
  fetchPage: (page: { limit: number; offset: number }) => Promise<{ meta: DataMeta; data: List }>
  /** A row's identity across pages (a stable function: the fold is memoised on it). */
  identify: (row: Row) => string
}) {
  const queryClient = useQueryClient()
  const result = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage({ limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, _pages, offset) => nextOffset(last.data, offset),
    placeholderData: keepPreviousData,
  })
  const folded = useMemo(() => (result.data ? foldPages<Row, List>(result.data.pages, identify) : undefined), [result.data, identify])
  /**
   * Every page is read, yet fewer rows are shown than the API counts: one moved up past a
   * page already read while the next loaded (paging by offset cannot see it).
   */
  const incomplete =
    folded !== undefined && !result.hasNextPage && !result.isFetching && folded.rows.length < folded.latest.total
  /** What the footer shows and does: the next page, and reading the list again from its first. */
  const paging: Paging = {
    hasMore: result.hasNextPage,
    loadingMore: result.isFetchingNextPage,
    moreFailed: result.isFetchNextPageError,
    incomplete,
    onMore: () => void result.fetchNextPage(),
    onRestart: () => void queryClient.resetQueries({ queryKey, exact: true }),
  }
  return { ...result, folded, paging }
}
